import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { SITE, NAV } from "@/lib/site";
import Logo from "@/components/Logo";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--ss-paper)] border-b border-[var(--ss-line)] shadow-[0_8px_24px_-18px_rgba(17,48,45,0.25)]"
          : "bg-[var(--ss-paper)] border-b border-transparent"
      }`}
    >
      {/* Top utility bar */}
      <div className="hidden md:block bg-[var(--ss-teal-800)] text-[var(--ss-cream)] text-xs">
        <div className="container-px max-w-7xl mx-auto flex items-center justify-between py-2">
          <span className="opacity-90">
            <i className="fa-solid fa-location-dot mr-2 text-[var(--ss-gold)]"></i>
            Premium home care across Epping Forest, Harlow &amp; Bishop&#39;s Stortford
          </span>
          <a
            href={SITE.phoneHref}
            data-testid="topbar-phone"
            className="opacity-90 hover:opacity-100 transition-opacity"
          >
            <i className="fa-solid fa-phone mr-2 text-[var(--ss-gold)]"></i>
            Call us: <span className="font-semibold tracking-wide">{SITE.phone}</span>
          </a>
        </div>
      </div>

      <div className="container-px max-w-7xl mx-auto flex items-center justify-between py-4">
        {/* Logo */}
        <Link to="/" data-testid="header-logo" className="group">
          <Logo variant="dark" size="md" testId="header-logo-mark" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-[var(--ss-teal-700)]"
                    : "text-[var(--ss-ink)] hover:text-[var(--ss-teal-700)]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          <Link
            to="/contact#enquiry-form"
            data-testid="header-cta-book"
            className="btn btn-gold hidden sm:inline-flex"
          >
            <i className="fa-regular fa-calendar-check"></i>
            Book Free Assessment
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
            className="lg:hidden w-11 h-11 rounded-full border border-[var(--ss-line)] flex items-center justify-center text-[var(--ss-teal-800)]"
          >
            <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[var(--ss-line)] bg-[var(--ss-paper)]"
        >
          <div className="container-px max-w-7xl mx-auto py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-lg text-base ${
                    isActive
                      ? "bg-[var(--ss-cream)] text-[var(--ss-teal-700)] font-semibold"
                      : "text-[var(--ss-ink)]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={SITE.phoneHref}
              data-testid="mobile-phone"
              className="mt-3 btn btn-primary"
            >
              <i className="fa-solid fa-phone"></i>
              Call {SITE.phone}
            </a>
            <Link
              to="/contact#enquiry-form"
              data-testid="mobile-cta-book"
              className="btn btn-gold"
            >
              <i className="fa-regular fa-calendar-check"></i>
              Book Free Assessment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
